import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AppBarWithButtons } from '../../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppSwitchWithLabel from '../../../../components/designe/app-switch-with-label/AppSwitchWithLabel';
import AppButton from '../../../../components/designe/buttons/app-button/AppButton';
import AppSelectWrapper from '../../../../components/designe/textfields/app-select-wrapper/AppSelectWrapper';
import AppHelpTooltipButton from '../../../../components/designe/tooltips/AppHelpTooltipButton';
import AppTimeSetterField from '../../../../components/designe/wheels/app-time-setter-field/AppTimeSetterField';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useUnsavedChangesGuard } from '../../../../hooks/useUnsavedChangesGuard';
import { TrainingSetItemStatusListMode } from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { useStyles } from './FunctionalityEdit.styles';
import { useFunctionalityEditComponent } from './useFunctionalityEditComponent';

export default function FunctionalityEdit() {
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { replace } = useNavigationStack();
  const {
    showSaveSetButton,
    setShowSaveSetButton,
    showUploadSetButton,
    setShowUploadSetButton,
    showExerciseUnits,
    setShowExerciseUnits,
    onboardingEnabled,
    setOnboardingEnabled,
    showTooltips,
    setShowTooltips,
    trainingSetItemStatusListMode,
    setTrainingSetItemStatusListMode,
    defaultSetItemRestSec,
    setDefaultSetItemRestSec,
    handleSave,
    isLoading,
    isDirty,
  } = useFunctionalityEditComponent();
  const { allowAndLeave, dialog } = useUnsavedChangesGuard({
    isDirty,
    leaveAction: () => replace(Paths.settings.toFunctionalityView),
  });

  const statusListModeOptions = useMemo(
    () => [
      {
        id: TrainingSetItemStatusListMode.all,
        name: t('settings.functionality.trainingSetItemStatusListMode_all'),
      },
      {
        id: TrainingSetItemStatusListMode.primary,
        name: t('settings.functionality.trainingSetItemStatusListMode_primary'),
      },
    ],
    [t],
  );

  const selectedStatusListModeOption = useMemo(
    () =>
      statusListModeOptions.find((o) => o.id === trainingSetItemStatusListMode),
    [statusListModeOptions, trainingSetItemStatusListMode],
  );

  return (
    <div className={classes.wrapper}>
      {dialog}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        className={classes.content}>
        <Box className={classes.themeRow}>
          <AppSwitchWithLabel
            fullWidth
            label={
              <Box className={classes.labelWithTooltip}>
                <span>{t('settings.showSaveSetButton')}</span>
                <AppHelpTooltipButton
                  text={t('settings.functionality.showSaveSetButtonHint')}
                />
              </Box>
            }
            value={showSaveSetButton}
            onChange={() => setShowSaveSetButton(!showSaveSetButton)}
            labelPlacement="start"
          />
        </Box>
        <Box className={classes.themeRow}>
          <AppSwitchWithLabel
            fullWidth
            label={
              <Box className={classes.labelWithTooltip}>
                <span>{t('settings.showUploadSetButton')}</span>
                <AppHelpTooltipButton
                  text={t('settings.functionality.showUploadSetButtonHint')}
                />
              </Box>
            }
            value={showUploadSetButton}
            onChange={() => setShowUploadSetButton(!showUploadSetButton)}
            labelPlacement="start"
          />
        </Box>
        <Box className={classes.themeRow}>
          <AppSwitchWithLabel
            fullWidth
            label={
              <Box className={classes.labelWithTooltip}>
                <span>{t('settings.functionality.onboardingEnabled')}</span>
                <AppHelpTooltipButton
                  text={t('settings.functionality.onboardingEnabledHint')}
                />
              </Box>
            }
            value={onboardingEnabled}
            onChange={() => setOnboardingEnabled(!onboardingEnabled)}
            labelPlacement="start"
          />
        </Box>
        <Box className={classes.themeRow}>
          <AppSwitchWithLabel
            fullWidth
            label={
              <Box className={classes.labelWithTooltip}>
                <span>{t('settings.functionality.showTooltips')}</span>
                <AppHelpTooltipButton
                  text={t('settings.functionality.showTooltipsHint')}
                />
              </Box>
            }
            value={showTooltips}
            onChange={() => setShowTooltips(!showTooltips)}
            labelPlacement="start"
          />
        </Box>
        <Box className={classes.themeRow}>
          <AppSwitchWithLabel
            fullWidth
            label={
              <Box className={classes.labelWithTooltip}>
                <span>{t('settings.functionality.showExerciseUnits')}</span>
                <AppHelpTooltipButton
                  text={t('settings.functionality.showExerciseUnitsHint')}
                />
              </Box>
            }
            value={showExerciseUnits}
            onChange={() => setShowExerciseUnits(!showExerciseUnits)}
            labelPlacement="start"
          />
        </Box>
        <Box>
          <AppSelectWrapper
            label={
              <Box className={classes.labelWithTooltip}>
                <span>
                  {t('settings.functionality.trainingSetItemStatusListMode')}
                </span>
                <AppHelpTooltipButton
                  text={t(
                    'settings.functionality.trainingSetItemStatusListModeHint',
                  )}
                />
              </Box>
            }
            options={statusListModeOptions}
            disableClear
            disableSearch
            defaultValue={selectedStatusListModeOption}
            name="trainingSetItemStatusListMode"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const v = e.target.value as unknown as {
                id: TrainingSetItemStatusListMode;
              };
              setTrainingSetItemStatusListMode(
                v?.id ?? TrainingSetItemStatusListMode.all,
              );
            }}
          />
        </Box>

        <Box className={classes.restRow}>
          <AppTimeSetterField
            label={
              <Box className={classes.labelWithTooltip}>
                <span>{t('settings.functionality.defaultSetItemRestSec')}</span>
                <AppHelpTooltipButton
                  text={t('settings.functionality.defaultSetItemRestSecHint')}
                />
              </Box>
            }
            value={defaultSetItemRestSec}
            onChange={(v) => setDefaultSetItemRestSec(Math.min(300, v))}
            fullWidth
            textFieldVariant="default"
            placeholder={t('common.notSet')}
            classes={{ textField: classes.restInput }}
            pickerOptions={{
              showHours: false,
              maxMinutes: 5,
              maxSeconds: 59,
              secondStep: 5,
            }}
          />
          <AppButton
            className={classes.resetButton}
            variant="outlined"
            color="primary"
            onClick={() => setDefaultSetItemRestSec(null)}>
            {t('common.reset')}
          </AppButton>
        </Box>

        <Box>
          <Box className={classes.buttonWrapper}>
            <AppBarWithButtons
              loading={isLoading}
              disabled={!isDirty}
              onCancelClick={allowAndLeave}
            />
          </Box>
        </Box>
      </form>
    </div>
  );
}
